<template>
  <template v-if="status === 'pending'">
    <PageSkeleton />
  </template>
  <template v-else>
    <PageHero v-if="data?.cmsPage?.hero" :content="data.cmsPage.hero" />
    <template v-for="section in data?.cmsPage?.sections" :key="section.id">
      <PagePromoBanner v-if="section.type === PageSectionType.PROMO_BANNER" :content="section" />
      <PageTestimonials v-if="section.type === PageSectionType.TESTIMONIALS" :content="section" />
      <PagePopularCategories v-if="section.type === PageSectionType.POPULAR_CATEGORIES" :content="section" />
      <PageProductSelection v-if="section.type === PageSectionType.PRODUCT_SELECTION" :content="section" />
      <PageTextWithImage v-if="section.type === PageSectionType.TEXT_WITH_IMAGE" :content="section" />
      <PageRichText v-if="section.type === PageSectionType.RICH_TEXT" :content="section" />
    </template>
  </template>
</template>

<script setup lang="ts">
import { digest } from 'ohash';

import type { PageProps } from '~/components/Page/types';
import type { Locale } from '~/stores/app/types';

import { useAppStore } from '~/stores/app/appStore';
import { useAuthStore } from '~/stores/auth/authStore';
import { useCmsStore } from '~/stores/cms/cmsStore';
import { type CmsPage, PageSectionType } from '~/stores/cms/types';
import { useCustomerStore } from '~/stores/customer/customerStore';

const runtimeConfig = useRuntimeConfig();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();
const { locale } = useI18n();

const cmsStore = useCmsStore();
const appStore = useAppStore();
const customerStore = useCustomerStore();
const authStore = useAuthStore();

const dataKey = computed(() => `cms-page-${props.pageResolver.data.slug}-${props.pageResolver.data.locale}`);

const props = defineProps<PageProps>();

const { data, status } = await useAsyncData<{ cmsPage: CmsPage | null }>(dataKey, async () => {
  const resolvedSlug = props.pageResolver.data.slug;
  const resolvedLocale = props.pageResolver.data.locale as Locale;

  return { cmsPage: await cmsStore.fetchCmsPage(resolvedSlug, resolvedLocale) };
});

onMounted(() => {
  dataLayer.push({
    country: appStore.currentCountryCode,
    event: 'page_view',
    hashed_email: customerStore.customer?.email ? digest(customerStore.customer?.email) : 'n/a',
    locale: locale.value,
    page_key: dataKey.value,
    user_logged: authStore.isLoggedIn,
  });
});

const title = cmsStore.cmsPage?.meta_title ? cmsStore.cmsPage.meta_title : cmsStore.cmsPage?.title;
const description = cmsStore.cmsPage?.meta_description;

useSeoMeta({
  description,
  ogDescription: description,
  ogImage: {
    url: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  },
  ogSiteName: runtimeConfig.public.baseUrl,
  ogTitle: title,
  ogType: 'website',
  title,
  twitterCard: 'summary',
  twitterDescription: description,
  twitterImage: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  twitterSite: runtimeConfig.public.xTwitterAccount,
  twitterTitle: title,
});
</script>
